import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from './store.tsx';
import * as passkeys from './passkeys.ts';
import * as native from './native.ts';
import { api, setTokens, type Me } from './api.ts';

/**
 * App lock — the thing that makes Face ID sign-in worth having.
 *
 * Signing in with a biometric means little if the app then stays open on a
 * phone handed to someone else. This covers the two ways a balance gets seen by
 * the wrong person:
 *
 *  1. The app switcher. iOS photographs the screen when an app goes to the
 *     background, and that snapshot shows in the multitasking carousel and can
 *     persist on disk. Blurring on `visibilitychange` — before the snapshot is
 *     taken — is the only defence a web app has.
 *
 *  2. Leaving it open. After a period away, the app asks who you are again.
 *
 * The lock is a local gate, not a security boundary: the session token is still
 * valid underneath. It is defence against the person holding the phone, which
 * is the realistic threat, and it is deliberately not sold as more than that.
 */

const PREF_KEY = 'fintrack.lock';
// How long the app may sit in the background before a return needs Face ID.
// Short enough that switching away to read a bank SMS and coming back does not
// hand an unlocked ledger to whoever picks the phone up next; long enough that
// flicking to another app mid-task is not punished.
const LOCK_AFTER_MS = 60 * 1000;

interface LockPreference {
  enabled: boolean;
}

export function readLockPreference(): LockPreference {
  try {
    const raw = localStorage.getItem(PREF_KEY);
    return raw ? { enabled: JSON.parse(raw).enabled === true } : { enabled: false };
  } catch {
    return { enabled: false };
  }
}

export function writeLockPreference(pref: LockPreference): void {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(pref)); } catch { /* private browsing */ }
}

export function AppLock({ children }: { children: React.ReactNode }): JSX.Element {
  const { t, locale, setUser } = useApp();
  const ar = locale === 'ar';

  const [enabled, setEnabled] = useState(() => readLockPreference().enabled);
  // Locked from the very first render when the lock is on.
  //
  // This component only mounts on a genuine launch — swiping the app away and
  // reopening it, or a reload. Returning from the background does not remount,
  // so this cannot fire on an app that was merely switched away from; that case
  // is handled by the timer below. Starting unlocked meant the lock had no
  // effect at all on the one action people think of as "closing the app".
  const [locked, setLocked] = useState(() => readLockPreference().enabled);
  // Separate from `locked`: the blur goes up the instant the app is backgrounded,
  // whether or not enough time has passed to demand an unlock.
  const [obscured, setObscured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hiddenAt = useRef<number | null>(null);

  // The preference lives in localStorage and is changed from Settings, which
  // does not re-render this component — so listen rather than read once.
  useEffect(() => {
    const sync = () => setEnabled(readLockPreference().enabled);
    window.addEventListener('fintrack:lockpref', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('fintrack:lockpref', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!enabled) { setObscured(false); setLocked(false); return; }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Must happen synchronously here: once the snapshot is taken it is too
        // late to hide anything.
        hiddenAt.current = Date.now();
        setObscured(true);
      } else {
        const away = Date.now() - (hiddenAt.current ?? Date.now());
        if (away >= LOCK_AFTER_MS) setLocked(true);
        else setObscured(false);
      }
    };

    const onHidden = () => { hiddenAt.current = Date.now(); setObscured(true); };
    const onShown = () => {
      const away = Date.now() - (hiddenAt.current ?? Date.now());
      if (away >= LOCK_AFTER_MS) setLocked(true);
      else setObscured(false);
    };

    document.addEventListener('visibilitychange', onVisibility);
    // pagehide fires on iOS in cases visibilitychange does not, e.g. swiping up.
    window.addEventListener('pagehide', () => setObscured(true));

    // In the native shell, iOS's own signal is the reliable one: pagehide
    // largely does not fire in a WKWebView, so a lock that depended on it
    // would simply never engage on the platform it exists for.
    let removeNative = () => {};
    let cancelled = false;
    void native.onAppStateChange((active) => (active ? onShown() : onHidden()))
      .then((remove) => { if (cancelled) remove(); else removeNative = remove; });

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      cancelled = true;
      removeNative();
    };
  }, [enabled]);

  const unlock = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // In the native app this is a local Face ID check, not a passkey
      // ceremony. The session underneath is still valid — re-minting it over
      // the network would make the lock unopenable on a plane, which is a
      // worse failure than the one it prevents.
      if (native.isNative()) {
        const ok = await native.biometricUnlock(
          ar ? 'افتح فينتراك' : 'Unlock Fintrack',
        );
        if (ok) { setLocked(false); setObscured(false); }
        return;
      }

      const result = await passkeys.login();
      setTokens(result.accessToken, result.refreshToken);
      setUser(await api.get<Me>('/me'));
      setLocked(false);
      setObscured(false);
    } catch (err) {
      if (!passkeys.wasCancelled(err)) {
        setError(ar ? 'تعذّر الفتح. حاول مرة أخرى.' : 'Could not unlock. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [ar, setUser]);

  return (
    <>
      {children}

      {/* Backdrop-filter over the live app rather than a replacement screen:
          nothing is unmounted, so returning is instant and no state is lost. */}
      {(obscured || locked) && (
        <div className="app-lock" role="presentation">
          <div className="app-lock__panel">
            <div style={{ fontSize: 34 }} aria-hidden>◈</div>
            {locked ? (
              <>
                <div className="strong" style={{ fontSize: 17 }}>
                  {ar ? 'فينتراك مقفل' : 'Fintrack is locked'}
                </div>
                <p className="small muted center" style={{ marginTop: 6, marginBottom: 16, maxWidth: 260 }}>
                  {ar ? 'افتح القفل للمتابعة.' : 'Unlock to see your finances.'}
                </p>
                {error && <p className="small" style={{ color: 'var(--negative)', marginBottom: 10 }}>{error}</p>}
                <button className="btn btn--primary" onClick={unlock} disabled={busy}>
                  {busy ? '…' : t('unlock')}
                </button>
                {/* Without this the lock is a trap. A passkey can be deleted,
                    or live on a phone that is no longer in the room, and there
                    would otherwise be no way past this screen at all. */}
                <button
                  className="btn btn--ghost btn--sm"
                  style={{ marginTop: 10 }}
                  disabled={busy}
                  onClick={() => { setTokens(null, null); setUser(null); }}
                >
                  {ar ? 'الدخول بكلمة المرور بدلاً من ذلك' : 'Use my password instead'}
                </button>
              </>
            ) : (
              <div className="small muted">{t('appName')}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
